/**
 * ThankYouPage.tsx — the confirmation, at a real URL of its own.
 *
 * Owner repo: @thegpservice/gpsclinics-portal. Slice: thank-you route +
 * post-payment scroll reset (2026-08-20).
 *
 * WHY IT IS A ROUTE AND NOT A STAGE: see `thankYouRoute.ts` — marketing needs a
 * URL change to fire a conversion on, and a component swap inside
 * `/:clinicSlug` never gave them one. The SCREEN itself is unchanged:
 * `OrderConfirmation` is the same component checkout rendered before, mounted
 * here instead, so this move cannot quietly restyle the moment a patient reads
 * after paying.
 *
 * ── NO ORDER IN HAND ⇒ NO CONFIRMATION ─────────────────────────────────────
 * Two people reach this URL without having bought anything: someone who types or
 * shares it, and someone whose browser dropped the history entry. Both must be
 * told the truth. Showing "Order received" to either would be a fabricated
 * confirmation of a purchase that does not exist — on a page whose entire job is
 * to be believed — and it would tell a patient their treatment is on the way
 * when nobody has prescribed anything.
 *
 * So with nothing in hand this renders a plainly-worded, non-confirming screen
 * with a way onward, and fires NO tracking event. It is deliberately not a
 * redirect: a redirect would either bounce a guest into a sign-in they cannot
 * complete (`/patients` is PATIENT-gated) or drop somebody who HAS just paid
 * back at the top of a journey they have already finished, which reads as "it
 * didn't work" and is how a patient ends up paying twice.
 *
 * A REFRESH IS NOT THAT CASE. `location.state` lives in the browser's own
 * history entry, so reloading this page keeps the confirmation — nobody is
 * stranded here by pressing refresh, which was the other half of the ask.
 *
 * ── THE CELEBRATION (CTO 2026-09-24) ──────────────────────────────────────
 * A signed-in, verified patient skips checkout's account step and lands here
 * straight from the card, so THIS is their post-payment moment: a just-completed
 * confirmation gets the illustration and the warm line (`OrderConfirmation`'s
 * `celebrate`) and one burst of confetti (none under reduced motion). The
 * confetti plays ONCE per confirmation:
 *   - not at all when the guest already had it in checkout's account-step modal
 *     — `celebrated` in the history-state hand-over, never the URL;
 *   - not on a reload, nor on a later visit in the same document: it needs the
 *     hand-over checkout made in THIS document (`checkoutHandedOverHere`, module
 *     memory). Never sessionStorage/localStorage — a "has it played" flag is not
 *     strictly necessary storage (UK PECR reg. 6);
 *   - never for a re-shown confirmation (`justCompleted: false`).
 * The illustration and the warm line are static and follow `justCompleted`
 * alone, so a reload still reads as the confirmation it is.
 *
 * ── THE CONVERSION EVENT READS THE SAME HAND-OVER (review, 2026-09-25) ─────
 * `checkout_complete` fires only for `justCompleted` AND an arrival from a
 * checkout in THIS document (`arrivedFromCheckout`). `justCompleted` alone was
 * not enough: it lives in the history entry, which survives a reload and a Back
 * from the full-document "Go to my dashboard" link when the page is not in the
 * bfcache. The new document's instance ref starts false, so every such re-mount
 * pushed a second `checkout_complete` for one order. The hand-over mark is
 * exact: `goToConfirmation` is the only navigation to this route, and it calls
 * `noteCheckoutHandOver()` for every just-completed hand-over before it
 * navigates, so a real arrival is never missed.
 */
import { useEffect, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { useAuth } from '../auth/AuthContext';
import { getWeightPhotoStatus } from '../api/journey';
import { trackCheckoutComplete } from '../tracking/dataLayer';
import { ZoneExitLink } from '../tracking/ZoneExitLink';
import { useNoIndex } from '../lib/noIndex';
import { ButtonArrow, buttonClass, buttonStyle, Help, PageFrame, Title } from '../ui/kit';
import { OrderConfirmation } from './OrderConfirmation';
import { WeightPhotoCard } from '../patients/WeightPhotoCard';
import { readThankYouNavState } from './thankYouRoute';
import { Confetti, checkoutHandedOverHere, settleCheckoutHandOver } from './Celebration';

export function ThankYouPage() {
  const location = useLocation();
  const order = readThankYouNavState(location.state);

  /* KEPT OUT OF SEARCH RESULTS. A confirmation page has nothing to offer a
     searcher and everything to confuse one: the only version of it worth reading
     is the one you arrive at from your own checkout, and the version a crawler
     would index is the "nothing to show here" screen below. `noindex` also keeps
     a URL that exists purely as a conversion signal out of the ranking picture
     for the clinic pages that are meant to be found. */
  useNoIndex();

  /* THE FRONT-END CONVERSION EVENT — ONCE, AND ONLY FOR A REAL ARRIVAL.
     A ref, not state: React 18 StrictMode mounts effects twice in development
     and preserves the component instance between the two runs, so a ref is the
     only guard that survives the second pass. Without it every developer build
     would double-count and the numbers marketing tunes against would be wrong in
     a way that never reproduces in production.

     `justCompleted` is what separates "the patient just finished paying" from
     "we are re-showing a confirmation for an order that completed earlier" (a
     mid-journey hard refresh resumes to a finished OrderCase and lands here).
     Only the first is a conversion; counting the second would inflate the funnel
     by every patient who reloads their tab.

     It is `checkout_complete` and NOT `purchase` — no money has been taken at
     this point on the hold route, and the real, deduped revenue event fires
     server-side on capture. The full reasoning is in `tracking/dataLayer.ts`;
     do not shorten it to a rename here.

     AND ONLY IN THE DOCUMENT CHECKOUT HANDED IT OVER IN (`arrivedFromCheckout`,
     read once at mount; see the header). A reload, or a Back from the
     full-document dashboard link, restores `justCompleted: true` from the
     history entry in a NEW document, and that is the same order, not a second
     conversion. */
  const [arrivedFromCheckout] = useState(checkoutHandedOverHere);
  const tracked = useRef(false);
  useEffect(() => {
    if (!order?.justCompleted || !arrivedFromCheckout || tracked.current) return;
    tracked.current = true;
    trackCheckoutComplete({ leadId: order.leadId });
  }, [order, arrivedFromCheckout]);

  /* THE DEFERRED WEIGHT PHOTO, ASKED FOR AT THE MOMENT IT IS MOST LIKELY TO BE
     GIVEN. A patient who chose "I'll add this before dispatch" in the journey is
     never closer to acting on it than the seconds after they pay — waiting for
     them to find the dashboard card is how the photo stays missing at review.
     Same fail-silent contract as the Dashboard read: a failed or empty status
     simply never shows the prompt, and it must never block or delay this
     confirmation. The read is gated on a hydrated PATIENT principal for a
     reason beyond tidiness: an authed 401 clears the session and routes to
     /auth (AuthContext), which would navigate a just-paid guest OFF the one
     page that confirms their order — and off the ZoneExitLink-tracked path.
     No session ⇒ no call, and silence is the correct answer. */
  const { me } = useAuth();
  const [photoPending, setPhotoPending] = useState(false);
  /* ONE STATUS READ PER VISIT, GUARDED THE SAME TWO WAYS AS THE CONVERSION
     EVENT ABOVE. `readThankYouNavState` builds a FRESH object on every render,
     so depending on `order` (or on `me`, a context object) re-runs this effect
     for renders where nothing relevant changed — starting with the
     `setPhotoPending` render this very effect causes — and each re-run is
     another authed GET. The deps are therefore the primitives the read actually
     turns on: whether an order is in hand, and the caller's role. The ref is
     the second lock, set only when the request is DISPATCHED (never on an
     early return, or it would swallow the fetch that must follow auth
     hydration): like `tracked` above it survives StrictMode's double effect
     run, and it holds even if a future edit puts an object back in the deps. */
  const photoStatusRequested = useRef(false);
  const orderInHand = order !== null;
  const role = me?.role;
  useEffect(() => {
    if (!orderInHand || role !== 'PATIENT' || photoStatusRequested.current) return;
    photoStatusRequested.current = true;
    getWeightPhotoStatus()
      .then((s) => setPhotoPending(s.photoPending))
      .catch(() => undefined);
  }, [orderInHand, role]);

  /* THE CELEBRATION — decoration only, decided from the hand-over and this
     document's memory, and nothing else (no device storage — see the header).
     Whether checkout handed the order over in THIS document is read ONCE, at
     mount, and settled straight after: reading it after the settle would switch
     the confetti off mid-burst, and a later mount is not the arrival. */
  const justCompleted = order?.justCompleted === true;
  const celebratedInCheckout = order?.celebrated === true;
  // `arrivedFromCheckout` is read above, beside the conversion event it also gates.
  useEffect(() => {
    settleCheckoutHandOver();
  }, []);
  const showConfetti = justCompleted && !celebratedInCheckout && arrivedFromCheckout;

  if (!order) {
    return (
      <PageFrame narrow className="gps-journey-root">
        <Title>There&rsquo;s nothing to show here</Title>
        <Help>
          This page shows your confirmation straight after you place an order. If you have just placed
          one, it is safe &mdash; you will find it, and what happens next, in your account, and we have
          emailed you a copy.
        </Help>
        {/* ZoneExitLink (a full document navigation) — leaving the tracked
            acquisition journey for the untracked patient portal, so the GTM
            container never rides into /patients. Same control the confirmation
            itself uses. */}
        <ZoneExitLink to="/patients" className={buttonClass('cta')} style={buttonStyle('cta')}>
          Go to my dashboard
          <ButtonArrow />
        </ZoneExitLink>
      </PageFrame>
    );
  }

  return (
    <PageFrame narrow className="gps-journey-root">
      {showConfetti ? <Confetti /> : null}
      <OrderConfirmation
        medicationName={order.medicationName}
        captureRoute={order.captureRoute}
        celebrate={justCompleted}
      />
      {/* Between the confirmation and everything else, not above it: the order
          confirmation is the page's one job and stays first. */}
      {photoPending ? <WeightPhotoCard /> : null}
    </PageFrame>
  );
}
